import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePOAs, type POA } from '../../../hooks/usePOAs'
import { useAuth } from '../../../contexts/AuthContext'

type POAStatus = POA['status']

const STATUS_LABELS: Record<POAStatus, string> = {
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
}

const STATUS_STYLES: Record<POAStatus, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  expiring_soon: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  expired: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

function getDaysUntilExpiry(validUntil: string): number {
  const now = new Date()
  const end = new Date(validUntil)
  return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '\u2014'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
}

export default function POADetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { poas, deletePOA } = usePOAs()
  const { user } = useAuth()
  const poa = poas.find(p => p.id === id)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const canDelete = !!user

  async function handleDelete() {
    console.log('handleDelete called. id:', id, 'poa:', poa?.id)
    if (!id || !poa) {
      console.log('No id or poa, returning')
      return
    }
    const confirmed = window.confirm(
      `Are you sure you want to delete this POA for ${poa.importer_name}?`
    )
    console.log('Confirmation result:', confirmed)
    if (!confirmed) return

    console.log('Delete confirmed, calling deletePOA with id:', id, 'email:', user?.email)
    setDeleting(true)
    setDeleteError(null)
    try {
      await deletePOA(id, user?.email ?? '')
      console.log('Delete successful, navigating to /customs/poa')
      navigate('/customs/poa')
    } catch (e) {
      console.error('Delete error caught:', e)
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete POA')
      setDeleting(false)
    }
  }

  if (!poa) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">POA not found.</p>
      </div>
    )
  }

  const daysLeft = poa.valid_until && !poa.is_open_ended
    ? getDaysUntilExpiry(poa.valid_until)
    : null

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <nav className="text-sm">
        <span className="text-gray-500 dark:text-gray-400">Customs</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500 dark:text-gray-400">POA Management</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 dark:text-white">{poa.importer_name}</span>
      </nav>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/customs/poa')}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{poa.importer_name}</h1>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[poa.status]}`}>
            {STATUS_LABELS[poa.status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/customs/poa/${id}/edit`)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Edit
          </button>
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {deleteError && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {deleteError}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-6">Details</h2>
        <div className="grid grid-cols-2 gap-x-12 gap-y-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">IMPORTER NAME</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{poa.importer_name || '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">EORI NUMBER</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{poa.eori_number || '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">VAT NUMBER</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{poa.vat_number || '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">SIGNATORY</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{poa.signatory_name || '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">COUNTRY</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{poa.importer_country || '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">VALID FROM</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(poa.signed_date)}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">VALID UNTIL</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">
              {poa.is_open_ended ? 'Open-ended' : (
                <>
                  {poa.valid_until_estimated ? (
                    <span className="italic text-gray-500 dark:text-gray-400">Est. {formatDate(poa.valid_until)}</span>
                  ) : (
                    formatDate(poa.valid_until)
                  )}
                  {poa.valid_until_estimated && (
                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(1 year from signing)</span>
                  )}
                </>
              )}
              {daysLeft !== null && daysLeft > 0 && daysLeft <= 30 && (
                <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">({daysLeft}d left)</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">UK LOCAL COMPANY</p>
            <p className="mt-1 text-sm">
              {poa.is_uk_local_company
                ? <span className="text-green-600 dark:text-green-400">Yes</span>
                : <span className="text-gray-500 dark:text-gray-400">No</span>
              }
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">PVA AUTHORISATION</p>
            <p className="mt-1 text-sm">
              {poa.is_pva
                ? <span className="text-green-600 dark:text-green-400">Yes</span>
                : <span className="text-gray-500 dark:text-gray-400">No</span>
              }
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">CDS APPROVED</p>
            <p className="mt-1 text-sm">
              {poa.is_cds_approved
                ? <span className="text-green-600 dark:text-green-400">Yes</span>
                : <span className="text-gray-500 dark:text-gray-400">No</span>
              }
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">AGENT</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{poa.agent_name || '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">ADDRESS</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-line">{poa.importer_address || '\u2014'}</p>
          </div>
        </div>
      </div>

      {poa.notes && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Notes</h2>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{poa.notes}</p>
        </div>
      )}

      {poa.drive_web_link && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Document</h2>
          <a
            href={poa.drive_web_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {poa.drive_file_name || 'View POA Document'}
          </a>
        </div>
      )}

      <div className="pt-2">
        <button
          onClick={() => navigate('/customs/poa')}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          \u2190 Back to POA Management
        </button>
      </div>
    </div>
  )
}
